import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Pencil, Trash2, Upload, X, GripVertical, Image } from "lucide-react";
import api, { formatPrice } from "@/lib/api";

const EMPTY = { name_kz: "", name_ru: "", description_kz: "", description_ru: "", house_type: "pool", capacity_min: 1, capacity_max: 5, price_per_hour: 0, amenities_kz: [], amenities_ru: [], photos: [], video_url: "", status: "active", sort_order: 0 };
const TYPE_LABELS = { pool: "С бассейном", family: "Семейный", spa: "СПА" };
const API_URL = process.env.REACT_APP_BACKEND_URL;

function PhotoManager({ photos, onChange }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    const newPhotos = [...photos];
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append("file", file);
        const token = localStorage.getItem("eco_token");
        const res = await fetch(`${API_URL}/api/admin/upload`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });
        if (!res.ok) throw new Error("Upload failed");
        const data = await res.json();
        newPhotos.push(`${API_URL}/api/files/${data.path}`);
      } catch (err) {
        alert(`Ошибка загрузки: ${file.name}`);
      }
    }
    onChange(newPhotos);
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const removePhoto = (idx) => {
    onChange(photos.filter((_, i) => i !== idx));
  };

  const handleDragStart = (idx) => setDragIdx(idx);
  const handleDragOver = (e) => e.preventDefault();
  const handleDrop = (targetIdx) => {
    if (dragIdx === null || dragIdx === targetIdx) return;
    const arr = [...photos];
    const [moved] = arr.splice(dragIdx, 1);
    arr.splice(targetIdx, 0, moved);
    onChange(arr);
    setDragIdx(null);
  };

  return (
    <div>
      <label className="text-xs text-muted-foreground mb-2 block">Фотографии</label>
      <div className="grid grid-cols-3 gap-2 mb-2">
        {photos.map((url, i) => (
          <div
            key={i}
            className="relative group aspect-square bg-muted border border-border overflow-hidden cursor-grab"
            draggable
            onDragStart={() => handleDragStart(i)}
            onDragOver={handleDragOver}
            onDrop={() => handleDrop(i)}
            data-testid={`photo-item-${i}`}
          >
            <img src={url} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
              <GripVertical className="w-4 h-4 text-white/70" />
              <button
                onClick={() => removePhoto(i)}
                className="w-7 h-7 bg-red-500/80 hover:bg-red-500 rounded-full flex items-center justify-center"
                data-testid={`remove-photo-${i}`}
              >
                <X className="w-3.5 h-3.5 text-white" />
              </button>
            </div>
            {i === 0 && (
              <span className="absolute top-1 left-1 text-[10px] bg-primary text-white px-1.5 py-0.5">
                Главное
              </span>
            )}
          </div>
        ))}
        <button
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="aspect-square border-2 border-dashed border-border hover:border-primary/40 flex flex-col items-center justify-center text-muted-foreground hover:text-primary transition-colors"
          data-testid="upload-photo-btn"
        >
          {uploading ? (
            <span className="text-xs">Загрузка...</span>
          ) : (
            <>
              <Upload className="w-5 h-5 mb-1" />
              <span className="text-[10px]">Добавить</span>
            </>
          )}
        </button>
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        multiple
        onChange={handleUpload}
        className="hidden"
        data-testid="photo-file-input"
      />
    </div>
  );
}

export default function AdminHouses() {
  const [houses, setHouses] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = () => api.get("/admin/houses").then((r) => setHouses(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const toggleStatus = async (h) => {
    const newStatus = h.status === "active" ? "disabled" : "active";
    try {
      await api.put(`/admin/houses/${h.id}/status`, { status: newStatus });
      load();
    } catch (e) {
      alert(e.response?.data?.detail || "Ошибка");
    }
  };

  const activateAll = async () => {
    if (!window.confirm("Открыть ВСЕ закрытые домики? Они появятся на сайте.")) return;
    try {
      const r = await api.post("/admin/houses/activate-all");
      alert(`Открыто домиков: ${r.data.activated}`);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || "Ошибка");
    }
  };

  const handleSave = async () => {
    setLoading(true);
    const data = {
      ...form,
      amenities_kz: typeof form.amenities_kz === "string" ? form.amenities_kz.split(",").map(s => s.trim()).filter(Boolean) : form.amenities_kz,
      amenities_ru: typeof form.amenities_ru === "string" ? form.amenities_ru.split(",").map(s => s.trim()).filter(Boolean) : form.amenities_ru,
      photos: Array.isArray(form.photos) ? form.photos : [],
    };
    try {
      if (editId) await api.put(`/admin/houses/${editId}`, data);
      else await api.post("/admin/houses", data);
      setDialogOpen(false); setForm(EMPTY); setEditId(null); load();
    } catch (e) { alert("Ошибка сохранения"); }
    setLoading(false);
  };

  const handleEdit = (h) => {
    setForm({
      ...h,
      amenities_kz: h.amenities_kz?.join(", ") || h.amenities?.join(", ") || "",
      amenities_ru: h.amenities_ru?.join(", ") || "",
      photos: h.photos || [],
    });
    setEditId(h.id);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Удалить домик?")) return;
    await api.delete(`/admin/houses/${id}`);
    load();
  };

  return (
    <div data-testid="admin-houses">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-serif text-3xl">Домики</h1>
        <div className="flex items-center gap-2">
          <Button onClick={activateAll} variant="outline" className="rounded-none border-green-500/30 text-green-400 hover:bg-green-500/10 gap-1.5 text-xs" data-testid="activate-all-houses-btn">
            Открыть все
          </Button>
        <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) { setForm(EMPTY); setEditId(null); } }}>
          <DialogTrigger asChild>
            <Button className="bg-primary text-primary-foreground rounded-none gap-2" data-testid="add-house-btn">
              <Plus className="w-4 h-4" /> Добавить
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-white/10 max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-serif">{editId ? "Редактировать" : "Новый домик"}</DialogTitle></DialogHeader>
            <div className="space-y-3 mt-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Название (KZ)</label>
                  <Input value={form.name_kz} onChange={(e) => setForm({ ...form, name_kz: e.target.value })} className="bg-background border-white/10 rounded-none" data-testid="house-name-kz" />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Название (RU)</label>
                  <Input value={form.name_ru} onChange={(e) => setForm({ ...form, name_ru: e.target.value })} className="bg-background border-white/10 rounded-none" data-testid="house-name-ru" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Описание (KZ)</label>
                  <Textarea value={form.description_kz} onChange={(e) => setForm({ ...form, description_kz: e.target.value })} className="bg-background border-white/10 rounded-none min-h-[60px]" />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Описание (RU)</label>
                  <Textarea value={form.description_ru} onChange={(e) => setForm({ ...form, description_ru: e.target.value })} className="bg-background border-white/10 rounded-none min-h-[60px]" />
                </div>
              </div>
              <Select value={form.house_type} onValueChange={(v) => setForm({ ...form, house_type: v })}>
                <SelectTrigger className="bg-background border-white/10 rounded-none" data-testid="house-type-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-white/10">
                  <SelectItem value="pool">С бассейном</SelectItem>
                  <SelectItem value="family">Семейный</SelectItem>
                  <SelectItem value="spa">СПА</SelectItem>
                </SelectContent>
              </Select>
              <div className="grid grid-cols-3 gap-3">
                <Input type="number" placeholder="Мин. гостей" value={form.capacity_min || ""} onChange={(e) => setForm({ ...form, capacity_min: e.target.value === "" ? 0 : parseInt(e.target.value) || 0 })} onFocus={(e) => e.target.select()} className="bg-background border-white/10 rounded-none" />
                <Input type="number" placeholder="Макс. гостей" value={form.capacity_max || ""} onChange={(e) => setForm({ ...form, capacity_max: e.target.value === "" ? 0 : parseInt(e.target.value) || 0 })} onFocus={(e) => e.target.select()} className="bg-background border-white/10 rounded-none" />
                <Input type="number" placeholder="Цена/час (тг)" value={form.price_per_hour || ""} onChange={(e) => setForm({ ...form, price_per_hour: e.target.value === "" ? 0 : parseFloat(e.target.value) || 0 })} onFocus={(e) => e.target.select()} className="bg-background border-white/10 rounded-none" data-testid="house-price-input" />
              </div>
              <Input placeholder="Удобства KZ (через запятую)" value={form.amenities_kz} onChange={(e) => setForm({ ...form, amenities_kz: e.target.value })} className="bg-background border-white/10 rounded-none" />
              <Input placeholder="Удобства RU (через запятую)" value={form.amenities_ru} onChange={(e) => setForm({ ...form, amenities_ru: e.target.value })} className="bg-background border-white/10 rounded-none" />

              <PhotoManager photos={Array.isArray(form.photos) ? form.photos : []} onChange={(p) => setForm({ ...form, photos: p })} />

              <Input placeholder="URL видео" value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })} className="bg-background border-white/10 rounded-none" />
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="bg-background border-white/10 rounded-none"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-white/10">
                  <SelectItem value="active">Активен</SelectItem>
                  <SelectItem value="maintenance">На обслуживании</SelectItem>
                  <SelectItem value="disabled">Выключен</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={handleSave} disabled={loading} className="w-full bg-primary text-primary-foreground rounded-none" data-testid="house-save-btn">
                {loading ? "Сохранение..." : "Сохранить"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {houses.map((h) => (
          <div key={h.id} className="bg-card border border-white/5 overflow-hidden" data-testid={`admin-house-${h.id}`}>
            {h.photos?.[0] ? (
              <div className="aspect-[16/9] overflow-hidden relative">
                <img src={h.photos[0]} alt="" className="w-full h-full object-cover" />
                {h.photos.length > 1 && (
                  <span className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-2 py-0.5 flex items-center gap-1">
                    <Image className="w-3 h-3" /> {h.photos.length}
                  </span>
                )}
              </div>
            ) : (
              <div className="aspect-[16/9] bg-muted flex items-center justify-center">
                <Image className="w-8 h-8 text-muted-foreground" />
              </div>
            )}
            <div className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs text-primary uppercase tracking-widest">{TYPE_LABELS[h.house_type] || h.house_type}</span>
                <span className={`text-xs px-2 py-0.5 ${h.status === "active" ? "bg-green-900/30 text-green-400" : "bg-red-900/30 text-red-400"}`}>
                  {h.status === "active" ? "Активен" : h.status}
                </span>
              </div>
              <h3 className="font-serif text-lg">{h.name_ru || h.name_kz || h.name || ""}</h3>
              <p className="text-xs text-muted-foreground mt-0.5">{h.name_kz || ""}</p>
              <p className="text-sm text-primary mt-1">{formatPrice(h.price_per_hour)} / час</p>
              <div className="flex gap-2 mt-3 flex-wrap">
                <Button size="sm" onClick={() => toggleStatus(h)} className={`rounded-none gap-1 text-xs ${h.status === "active" ? "bg-red-600/20 text-red-400 hover:bg-red-600/30" : "bg-green-600/20 text-green-400 hover:bg-green-600/30"}`} data-testid={`toggle-house-${h.id}`}>
                  {h.status === "active" ? "Закрыть" : "Открыть"}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => handleEdit(h)} className="text-muted-foreground hover:text-foreground" data-testid={`edit-house-${h.id}`}>
                  <Pencil className="w-3 h-3 mr-1" /> Ред.
                </Button>
                <Button size="sm" variant="ghost" onClick={() => handleDelete(h.id)} className="text-red-400 hover:text-red-300" data-testid={`delete-house-${h.id}`}>
                  <Trash2 className="w-3 h-3 mr-1" /> Уд.
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
